/**
 * Ponte entre o botão como a tela edita (tempos "MM:SS", `buttonType` em pt-BR, posição em
 * três campos) e o item que a API grava em `tb_videos.actionButtons`.
 */
import type { ActionButton } from '@/store/videoFeatures/types/overlay-features';
import type { ActionButtonTemplate } from '@/store/videoFeatures/types/action-button-templates';
import { normalizeHexColor } from '@/lib/colors';
import { formatActionButtonPosition, parseActionButtonPosition } from './position';
import { hasTemplateContent, hydrateTemplate } from './model-switch';
import { mmssToSeconds, secondsToMMSS } from './list';

export interface ApiActionButton {
  id: string;
  enabled: boolean;
  mode: 'below' | 'over' | 'html';
  text: string;
  link: string;
  size: 'pequeno' | 'medio' | 'grande';
  /** Segundos. `end: null` = até o fim do vídeo. */
  timing: { showThroughout: boolean; start: number; end: number | null };
  style: {
    background: string;
    text: string;
    hoverBackground: string;
    hoverText: string;
    borderRadius: number | null;
    shadow: boolean;
    pulse: boolean;
  };
  position: string;
  customHtml: string;
  template: ActionButtonTemplate | null;
}

const MODE_TO_API = { 'abaixo-video': 'below', 'sobre-video': 'over', html: 'html' } as const;
const MODE_FROM_API = { below: 'abaixo-video', over: 'sobre-video', html: 'html' } as const;
const SIZE_TO_API = { small: 'pequeno', medium: 'medio', large: 'grande' } as const;
const SIZE_FROM_API = { pequeno: 'small', medio: 'medium', grande: 'large' } as const;

export function toApiActionButton(button: ActionButton): ApiActionButton {
  const timing = button.timing || {};
  const showThroughout = timing.showThroughout === true;
  return {
    id: button.id || '',
    enabled: button.enabled === true,
    mode: MODE_TO_API[button.buttonType || 'abaixo-video'] ?? 'below',
    text: button.text || '',
    link: (button.url ?? button.link ?? '').trim(),
    size: SIZE_TO_API[button.size || 'medium'] ?? 'medio',
    timing: {
      showThroughout,
      start: showThroughout ? 0 : mmssToSeconds(timing.start) ?? 0,
      end: showThroughout ? null : mmssToSeconds(timing.end),
    },
    style: {
      background: button.style?.backgroundColor || button.backgroundColor || '#1D4FFF',
      text: button.style?.textColor || button.textColor || '#FFFFFF',
      hoverBackground: button.style?.hoverBackgroundColor || button.style?.backgroundColor || '#1A47E5',
      hoverText: button.style?.hoverTextColor || button.style?.textColor || '#FFFFFF',
      borderRadius: button.style?.borderRadius ?? 8,
      shadow: button.style?.shadow !== false,
      pulse: button.style?.pulse === true,
    },
    position: formatActionButtonPosition(button.position, button.horizontalOffset, button.verticalOffset),
    // `??` e não `||`: apagar o código (html === '') tem de gravar vazio.
    customHtml: button.html ?? button.customHtml ?? '',
    // Esqueleto do Padrão não vai: o player desenharia o bloco de oferta em vez do botão simples.
    template: hasTemplateContent(button.template) ? button.template : null,
  };
}

export function fromApiActionButton(item: ApiActionButton): ActionButton {
  const size = SIZE_FROM_API[item.size] ?? 'medium';
  const position = parseActionButtonPosition(item.position);
  // O ColorPicker só aceita hex; vazio (botão migrado) usa a cor padrão do editor.
  const background = normalizeHexColor(item.style?.background, '#1D4FFF');
  const text = normalizeHexColor(item.style?.text, '#FFFFFF');
  const hoverBackground = normalizeHexColor(item.style?.hoverBackground, '#1A47E5');
  const hoverText = normalizeHexColor(item.style?.hoverText, '#FFFFFF');
  const showThroughout = item.timing?.showThroughout === true;
  const start = item.timing?.start ?? 0;
  const end = item.timing?.end ?? null;
  return {
    id: item.id,
    enabled: item.enabled !== false,
    buttonType: MODE_FROM_API[item.mode] ?? 'abaixo-video',
    text: item.text || '',
    url: item.link || '',
    link: item.link || '',
    size,
    position: position.position,
    horizontalOffset: position.horizontalOffset,
    verticalOffset: position.verticalOffset,
    template: hydrateTemplate(item.template),
    backgroundColor: background,
    textColor: text,
    html: item.customHtml || '',
    customHtml: item.customHtml || '',
    timing: {
      showThroughout,
      start: showThroughout || start === 0 ? '' : secondsToMMSS(start),
      end: showThroughout || end === null ? '' : secondsToMMSS(end),
    },
    style: {
      size,
      backgroundColor: background,
      textColor: text,
      hoverBackgroundColor: hoverBackground,
      hoverTextColor: hoverText,
      // `?? 8`: raio 0 (canto reto) é escolha válida.
      borderRadius: item.style?.borderRadius ?? 8,
      shadow: item.style?.shadow !== false,
      pulse: item.style?.pulse === true,
    },
    colors: { background, text, backgroundHover: hoverBackground, textHover: hoverText },
  };
}

/** A lista do vídeo vinda da API; ausente vira []. */
export function actionButtonsFromVideo(videoData: { actionButtons?: unknown } | null | undefined): ActionButton[] {
  const list = videoData?.actionButtons;
  return Array.isArray(list) ? (list as ApiActionButton[]).map(fromApiActionButton) : [];
}
